/// <amd-module name="Agile/Common/Utils/QueuedRequestManager/RequestNode" />
import {
    EntityID,
    IEntityReference,
    IRequestExecutorData,
    IRequestMetadata,
    RequestCompletionBehavior,
    RequestState
} from "Agile/Common/Utils/QueuedRequestManager/QueuedRequestManager.types";
import { stringifyEntityReference } from "Agile/Common/Utils/QueuedRequestManager/Utils";

/**
 * (internal) Container for the data stored with each predecessor in a RequestNode
 */
export interface IPredecessorData {
    node: RequestNode<any>;
    entityRefToUpdate: IEntityReference | null;
}

/**
 * (internal) A single node in the request graph. Converts the external caller's
 * parameters into private data.
 */
export class RequestNode<TResult> {
    private _stringifiedKey: string;
    private _targetEntity: IEntityReference;
    private _predecessorTypes: string[];
    private _successBehavior: RequestCompletionBehavior;
    private _failureBehavior: RequestCompletionBehavior;

    /**
     * References to nodes which are dependent on this one
     */
    public successorNodes: RequestNode<any>[] = [];

    /**
     * Data for each node that this one depends on. "IPredecessorData" includes
     * the name which will be used by the caller to identify the entity.
     */
    public predecessorNodes: IPredecessorData[] = [];

    private _executeRequest: () => Promise<TResult>;
    private _getUpdatedId?: (requestResult: TResult) => EntityID;
    private _resolve: (value?: TResult | PromiseLike<TResult>) => void;
    private _reject: (reason?: any) => void;

    public state: RequestState = RequestState.Pending;

    constructor(
        originalRequestMetadata: IRequestMetadata<TResult>,
        originalExecutor: IRequestExecutorData<TResult>,
        resolve: (value?: TResult | PromiseLike<TResult>) => void,
        reject: (reason?: any) => void
    ) {
        this._stringifiedKey = stringifyEntityReference(originalRequestMetadata.targetEntity);
        this._predecessorTypes = originalRequestMetadata.predecessorTypes || [];
        this._targetEntity = originalRequestMetadata.targetEntity;
        this._successBehavior =
            originalRequestMetadata.successBehavior == null ? RequestCompletionBehavior.Continue : originalRequestMetadata.successBehavior;
        this._failureBehavior =
            originalRequestMetadata.failureBehavior == null ? RequestCompletionBehavior.CancelQueued : originalRequestMetadata.failureBehavior;
        this._getUpdatedId = originalRequestMetadata.getUpdatedId;
        this._executeRequest = originalExecutor.executeRequest;

        this._resolve = resolve;
        this._reject = reject;
    }

    public get entityId(): EntityID {
        return this._targetEntity.entityId;
    }

    public get entityType(): string {
        return this._targetEntity.entityType;
    }

    public get entityKey(): string {
        return this._stringifiedKey;
    }

    public get predecessorTypes(): string[] {
        return this._predecessorTypes;
    }

    public get successBehavior(): RequestCompletionBehavior {
        return this._successBehavior;
    }

    public get failureBehavior(): RequestCompletionBehavior {
        return this._failureBehavior;
    }

    public get isPending(): boolean {
        return this.state === RequestState.Pending;
    }

    public get isExecuting(): boolean {
        return this.state === RequestState.Executing;
    }

    public get isSettled(): boolean {
        return this.state === RequestState.Settled;
    }

    public invokeResolve(value?: TResult): void {
        this._resolve(value);
    }

    public invokeReject(reason?: any): void {
        this._reject(reason);
    }

    public executeRequest(): Promise<TResult> {
        return this._executeRequest();
    }

    public swapEntityId(newId: EntityID) {
        this._targetEntity.entityId = newId;
        this._stringifiedKey = stringifyEntityReference(this);
    }

    public evaluateGetUpdatedEntityId(requestResult: TResult): EntityID {
        if (!this._getUpdatedId) {
            return this._targetEntity.entityId;
        }

        const newId = this._getUpdatedId(requestResult);

        if (newId == null) {
            return this._targetEntity.entityId;
        }

        return newId;
    }

    /**
     * Removes the given predecessor from the supplied node and stores its
     * current ID in the successor's "finalPredecessorIdsByName".
     * @param successor The node which has a predecessor to be removed.
     * @param predecessorNode The predecessor to remove.
     */
    public removePredecessor(successor: RequestNode<any>) {
        const indexInPredecessors = successor.predecessorNodes.findIndex(predecessor => predecessor.node === this);
        if (indexInPredecessors < 0) {
            throw new Error("The specified node was not found as a predecessor to this node.");
        }

        const predecessorEntry = successor.predecessorNodes[indexInPredecessors];
        if (predecessorEntry.entityRefToUpdate) {
            // Filter out the implicit predecessor on the previous request on this entity
            // Update the entity ref, which allows the caller to fetch the updated ID later
            predecessorEntry.entityRefToUpdate.entityId = this.entityId;
        }

        successor.predecessorNodes.splice(indexInPredecessors, 1);
    }
}
